import type { QuantityPricePair, PLVProduct } from '../types/plv.types';

export function calculateUnitPrice(quantityPricePairs: QuantityPricePair[], quantity: number): number {
  if (!quantityPricePairs || quantityPricePairs.length === 0) return 0;

  const sortedPairs = [...quantityPricePairs].sort((a, b) => a.quantity - b.quantity);

  const exactMatch = sortedPairs.find(pair => pair.quantity === quantity);
  if (exactMatch) return exactMatch.price / exactMatch.quantity;

  const closestPair = sortedPairs.filter(pair => pair.quantity <= quantity).pop();
  if (closestPair) return closestPair.price / closestPair.quantity;

  const firstPair = sortedPairs[0];
  return firstPair ? firstPair.price / firstPair.quantity : 0;
}

export function calculateTotalPrice(quantityPricePairs: QuantityPricePair[], quantity: number): number {
  if (!quantityPricePairs || quantityPricePairs.length === 0) return 0;

  const sortedPairs = [...quantityPricePairs].sort((a, b) => a.quantity - b.quantity);

  const exactMatch = sortedPairs.find(pair => pair.quantity === quantity);
  if (exactMatch) return exactMatch.price;

  const closestPair = sortedPairs.filter(pair => pair.quantity <= quantity).pop();
  if (closestPair) return (closestPair.price / closestPair.quantity) * quantity;

  const firstPair = sortedPairs[0];
  return firstPair ? (firstPair.price / firstPair.quantity) * quantity : 0;
}

export function calculateTotalPriceForProduct(product: PLVProduct, quantity: number): number {
  return calculateTotalPrice(product.quantityPricePairs, quantity);
}

export function calculateTotalPriceFromCart(product: PLVProduct, quantity: number): number {
  return calculateTotalPrice(product.quantityPricePairs, quantity);
}

export function getBestUnitPrice(quantityPricePairs: QuantityPricePair[]): number {
  if (!quantityPricePairs || quantityPricePairs.length === 0) return 0;
  const unitPrices = quantityPricePairs.map(pair => pair.price / pair.quantity);
  return Math.min(...unitPrices);
}

export function getBasePriceHt(quantityPricePairs: QuantityPricePair[]): number {
  if (!quantityPricePairs || quantityPricePairs.length === 0) return 0;
  const sortedPairs = [...quantityPricePairs].sort((a, b) => a.quantity - b.quantity);
  const firstPair = sortedPairs[0];
  return firstPair.price / firstPair.quantity;
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
}

export function getPricingInfo(product: PLVProduct): {
  hasMultiplePairs: boolean;
  basePrice: number;
  bestPrice: number;
  priceRange: string;
} {
  const hasMultiplePairs = product.quantityPricePairs.length > 1;
  const basePrice = getBasePriceHt(product.quantityPricePairs);
  const bestPrice = getBestUnitPrice(product.quantityPricePairs);

  let priceRange = '';
  if (hasMultiplePairs && basePrice !== bestPrice) {
    priceRange = `${formatPrice(bestPrice)} - ${formatPrice(basePrice)}`;
  } else {
    priceRange = formatPrice(basePrice);
  }

  return { hasMultiplePairs, basePrice, bestPrice, priceRange };
}

export function calculateSavings(quantityPricePairs: QuantityPricePair[], quantity: number): {
  unitSavings: number;
  totalSavings: number;
  percentageSavings: number;
} {
  const basePrice = getBasePriceHt(quantityPricePairs);
  const actualPrice = calculateUnitPrice(quantityPricePairs, quantity);
  const unitSavings = basePrice - actualPrice;
  const totalSavings = unitSavings * quantity;
  const percentageSavings = basePrice > 0 ? (unitSavings / basePrice) * 100 : 0;

  return {
    unitSavings: Math.max(0, unitSavings),
    totalSavings: Math.max(0, totalSavings),
    percentageSavings: Math.max(0, percentageSavings),
  };
}
